import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faChartSimple } from "@fortawesome/pro-regular-svg-icons";
import { twMerge } from "tailwind-merge";
import type { TopLevelSpec } from "vega-lite";

import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import { TAKTILE_CHART_CONFIG } from "src/analytics/metrics/VisualizeMetrics/liveAnalyticsDomains";
import {
  applySpecialDomainBindings,
  type LabelDictionaries,
} from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import {
  type FromFrame,
  isReadyNotification,
  isRenderError,
  isRenderResult,
  JSONRPC_VERSION,
  METHOD_INIT,
  METHOD_RENDER,
} from "src/chartSandbox/protocol";
import { EmptyState } from "src/design-system/EmptyState";

export type TaktileChartConfig = typeof TAKTILE_CHART_CONFIG;

const ChartConfigContext =
  createContext<TaktileChartConfig>(TAKTILE_CHART_CONFIG);

/** Override platform Vega defaults for a subtree (e.g. Case Analytics bar width). */
export const ChartConfigProvider = ChartConfigContext.Provider;

const useChartConfig = (): TaktileChartConfig => useContext(ChartConfigContext);

// Module-level constant so `<SandboxedChart />` callers that omit the prop
// don't allocate a fresh `{}` per render and bust IframeChart's useMemo.
const EMPTY_LABEL_DICTIONARIES: LabelDictionaries = {};

const VEGA_SPEC_DISCRIMINATORS = [
  "mark",
  "layer",
  "facet",
  "repeat",
  "concat",
  "vconcat",
  "hconcat",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const isVegaLiteSpec = (
  chartDefinition: Record<string, unknown>,
): boolean => VEGA_SPEC_DISCRIMINATORS.some((key) => key in chartDefinition);

export const extractTitle = (
  chartDefinition: Record<string, unknown>,
): string => {
  const { title } = chartDefinition;
  if (typeof title === "string") return title;
  if (isRecord(title) && typeof title.text === "string") return title.text;
  return "";
};

// Shallow-merge each object-valued section of the platform chart config with
// the matching LLM-provided section so a partial override (`axis: { labelFont }`)
// doesn't wipe out our other section defaults (labelColor, gridColor, …).
export const mergeChartConfig = (
  userConfig: Record<string, unknown>,
  baseConfig: TaktileChartConfig = TAKTILE_CHART_CONFIG,
): Record<string, unknown> => {
  const subsections = Object.fromEntries(
    Object.entries(baseConfig)
      .filter(([, base]) => isRecord(base))
      .map(([key, base]) => [
        key,
        {
          ...(base as Record<string, unknown>),
          ...(isRecord(userConfig[key]) ? userConfig[key] : {}),
        },
      ]),
  );
  return { ...baseConfig, ...userConfig, ...subsections };
};

export const buildSpec = (
  chartDefinition: Record<string, unknown>,
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries,
  chartConfig: TaktileChartConfig,
): TopLevelSpec => {
  const withDomains = applySpecialDomainBindings(
    chartDefinition as unknown as TopLevelSpec,
    rows,
    labelDictionaries,
  ) as unknown as Record<string, unknown>;
  return {
    ...withDomains,
    width: "container",
    height: "container",
    autosize: { contains: "padding" },
    title: null,
    config: {
      ...mergeChartConfig(
        isRecord(withDomains.config) ? withDomains.config : {},
        chartConfig,
      ),
      // See OPTI-7076.
      customFormatTypes: false,
    },
    data: { values: rows },
  } as unknown as TopLevelSpec;
};

// Fail loud with an error state if the frame never reports back within this window.
const CHART_TIMEOUT_MS = 10_000;

// Deny every powerful feature to the sandboxed frame; it only draws a canvas.
const DENIED_FEATURES = [
  "accelerometer",
  "autoplay",
  "camera",
  "clipboard-read",
  "clipboard-write",
  "display-capture",
  "fullscreen",
  "geolocation",
  "gyroscope",
  "hid",
  "magnetometer",
  "microphone",
  "midi",
  "payment",
  "picture-in-picture",
  "publickey-credentials-get",
  "screen-wake-lock",
  "serial",
  "usb",
  "web-share",
  "xr-spatial-tracking",
];

const FRAME_PERMISSIONS_POLICY = DENIED_FEATURES.map(
  (feature) => `${feature} 'none'`,
).join("; ");

const IframeChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
  labelDictionaries: LabelDictionaries;
  chartConfig: TaktileChartConfig;
}> = ({ rows, chartDefinition, labelDictionaries, chartConfig }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const portRef = useRef<MessagePort | null>(null);
  const renderIdRef = useRef(0);
  const watchdogRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<"loading" | "rendered" | "error">(
    "loading",
  );
  const hasError = status === "error";
  const [frameUrl, setFrameUrl] = useState<string>();

  // Lazy-load the frame document (bundled Vega) so it stays out of the app's
  // main chunk; the blob is same-origin, so this import needs no CORS.
  useEffect(() => {
    let active = true;
    void import("src/chartSandbox/frameDocument").then((module) => {
      if (active) setFrameUrl(module.FRAME_BLOB_URL);
    });
    return () => {
      active = false;
    };
  }, []);

  const spec = useMemo(
    () => buildSpec(chartDefinition, rows, labelDictionaries, chartConfig),
    [chartDefinition, rows, labelDictionaries, chartConfig],
  );

  // A per-render watchdog: every render (initial and re-render) must report back
  // within the window or we surface an error, so a hung render can't silently
  // leave a stale chart on screen. Cleared once the matching reply arrives.
  const armWatchdog = useCallback((): void => {
    clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(
      () => setStatus("error"),
      CHART_TIMEOUT_MS,
    );
  }, []);

  const handleLoad = useCallback((): void => {
    setReady(false); // a reload needs a fresh handshake before we resend the spec
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    const channel = new MessageChannel();
    portRef.current = channel.port1;
    channel.port1.onmessage = (event: MessageEvent<FromFrame>): void => {
      const message = event.data;
      if (isReadyNotification(message)) {
        setReady(true);
        return;
      }
      if (message.id !== renderIdRef.current) return; // superseded render
      if (isRenderResult(message)) {
        clearTimeout(watchdogRef.current);
        setStatus("rendered");
      } else if (isRenderError(message)) {
        clearTimeout(watchdogRef.current);
        setStatus("error");
      }
    };
    // "*": the frame's opaque origin can't be named as a targetOrigin; safe —
    // posted to our own contentWindow, and the payload holds no secrets.
    frame.postMessage({ jsonrpc: JSONRPC_VERSION, method: METHOD_INIT }, "*", [
      channel.port2,
    ]);
  }, []);

  useEffect(() => {
    if (!ready || !portRef.current) return;
    const id = (renderIdRef.current += 1);
    setStatus((current) => (current === "error" ? "loading" : current));
    portRef.current.postMessage({
      jsonrpc: JSONRPC_VERSION,
      id,
      method: METHOD_RENDER,
      params: { spec },
    });
    armWatchdog();
  }, [ready, spec, armWatchdog]);

  // Arm on mount too, so a hung load or handshake (before any render is posted)
  // still surfaces an error instead of a permanent blank frame.
  useEffect(() => {
    armWatchdog();
    return () => clearTimeout(watchdogRef.current);
  }, [armWatchdog]);

  useEffect(() => () => portRef.current?.close(), []);

  return (
    // The frame's DOM is sealed off, so render progress is only knowable here.
    <div
      className="relative h-full w-full"
      data-chart-state={status}
      data-loc="sandboxed-chart"
    >
      {frameUrl && (
        <iframe
          ref={iframeRef}
          allow={FRAME_PERMISSIONS_POLICY}
          className={twMerge(
            "h-full w-full border-0",
            status !== "rendered" && "invisible",
          )}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts"
          src={frameUrl}
          title="Chart"
          onLoad={handleLoad}
        />
      )}
      {hasError && (
        <div className="absolute inset-0">
          <EmptyState
            description="The chart could not be rendered."
            headline="Unable to render chart"
            icon={faChartSimple}
            size="compact"
            variant="error"
          />
        </div>
      )}
    </div>
  );
};

export const SandboxedChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
  labelDictionaries?: LabelDictionaries;
}> = ({
  rows,
  chartDefinition,
  labelDictionaries = EMPTY_LABEL_DICTIONARIES,
}) => {
  const chartConfig = useChartConfig();

  if (!isVegaLiteSpec(chartDefinition)) {
    return (
      <EmptyState
        description="The chart definition is missing a mark or layer."
        headline="Invalid chart definition"
        icon={faChartSimple}
        size="compact"
        variant="error"
      />
    );
  }

  return (
    <IframeChart
      chartConfig={chartConfig}
      chartDefinition={chartDefinition}
      labelDictionaries={labelDictionaries}
      rows={rows}
    />
  );
};
